import React, { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { nameOf, HOUSING_SITUATION_LABEL, UNRECOGNISED_WORD } from "@/lib/format";

// THE SCHOOL OF AN EXPENSE, AND ITS COA PERIOD (ZEN-460 / ZEN-58).
//
// Maryse, 15 Oct 2026: the app picks the school from the date paid; the box
// stays editable but offers only schools that could apply to the year paid.
// The list, the pick and the period all come from the server's one rule
// (GET /expenses/school-choices) — this screen keeps no copy of it. Used by the
// expense form and the Approvals screen alike.

export const SCHOOL_BOX_NOTE = "Choose another school if this expense isn't for this one.";
export const noSchoolRuns = (name, year) => `No school on ${name}'s card runs in ${year}.`;

/** The school a form may name to the server: the person's own choice, or the
 * stored school an edit keeps. Anything else is the app's pick, so nothing is
 * named and the server picks from the date. */
export const namedSchool = (chosenRef, keep, value) => ((chosenRef?.current || keep) ? (value || "") : "");

export function useSchoolChoices({ studentId, date, category, schoolId, enabled = true }) {
  const day = /^\d{4}-\d{2}-\d{2}$/.test((date || "").slice(0, 10)) ? date.slice(0, 10) : "";
  return useQuery({
    queryKey: ["school-choices", studentId, day, category || "", schoolId || ""],
    queryFn: async () => (await api.get("/expenses/school-choices", {
      params: { student_id: studentId, transaction_date: day, category: category || undefined,
                school_enrollment_id: schoolId || undefined },
    })).data,
    enabled: enabled && !!studentId && !!day,
  });
}

const MONTH = (ym) => {
  const [y, m] = (ym || "").split("-").map(Number);
  if (!y || !m) return ym || "";
  return `${new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "long", timeZone: "UTC" })} ${y}`;
};

// Approved wording (14 Oct 2026): the period's own extent in words, and the
// housing situation only when it says something — "Other" is dropped.
export function periodLabel(p) {
  if (!p) return "";
  const range = p.from_month === p.to_month ? MONTH(p.from_month)
    : `${MONTH(p.from_month)} – ${MONTH(p.to_month)}`;
  if (!p.situation || p.situation === "other") return range;
  return `${range} · ${nameOf(HOUSING_SITUATION_LABEL, p.situation, p.situation_custom || UNRECOGNISED_WORD)}`;
}

const schoolText = (s) => `${s.school_name}${s.student_school_id ? ` (${s.student_school_id})` : ""}`;

export function SchoolEnrollmentBox({ choices, value, onChange, studentName, year, disabled,
                                      testid = "exp-school", extraOption }) {
  // Not yet answered (loading, or the request failed) is not "no school runs":
  // only the server's own empty list may say that.
  const answered = Array.isArray(choices);
  const list = choices || [];
  const options = extraOption && !list.some((s) => s.id === extraOption.id) ? [...list, extraOption] : list;
  return (
    <div className="block">
      <span id={`${testid}-label`} className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">School enrollment</span>
      {options.length === 0 ? (
        <div data-testid={testid}
          className="flex h-10 w-full items-center rounded-md border border-[#E9E9EB] bg-[#F7F8FA] px-3 text-sm text-[#374151]">—</div>
      ) : (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} disabled={disabled} aria-labelledby={`${testid}-label`}
          className="h-10 w-full rounded-md border border-[#C2C9D1] bg-white px-2 text-sm focus:border-[#034AA2] focus:outline-none focus:ring-2 focus:ring-[#034AA2]/30 disabled:bg-[#E9E9EB]">
          {!value && <option value="" disabled>Choose which one this expense belongs to.</option>}
          {options.map((s) => <option key={s.id} value={s.id}>{schoolText(s)}</option>)}
        </select>
      )}
      <p className="mt-1 text-[11px] text-[#6b7280]" data-testid={`${testid}-note`}>
        {answered && list.length === 0 && studentName && year ? noSchoolRuns(studentName, year) : SCHOOL_BOX_NOTE}
      </p>
    </div>
  );
}

// Keeps the form's school in step with the server's pick until the person
// chooses one; a chosen school stays while it still applies. `keepStored` is
// true while an edit has the expense's own date and beneficiary: the server
// keeps the stored school then (Q4), so the form shows and sends `stored` —
// including after the date is changed and changed back.
//
// ONE RULE, RE-ASKED WHENEVER ANY INPUT CHANGES, the form's own value included
// (review round 7). It used to run only when the server's answer changed, so a
// value the form reset on reopening was never re-filled. The school it should
// show is, in order:
//   1. the person's own choice, while it is one the server offers for this
//      date, or is the stored school of an edit keeping its date — the server
//      honours a named school it offers, so the form sends what it will store;
//   2. the stored school, while the edit keeps its date and beneficiary;
//   3. the server's pick for this date.
// It writes only when the value differs, so it settles in one step.
//
// WHO NAMED THE SCHOOL (review round 9). The server honours a school the
// request names, so a form must name one only when the person chose it (or it
// is the stored school an edit keeps) — never a school the app put in itself.
// The form passes `chosenRef` and reads it, through `namedSchool`, both for
// the school it asks the server about and for the school it saves.
export function useFollowThePick({ data, value, setValue, resetKey, keepStored, stored, chosenRef }) {
  const own = useRef(false);
  const chosen = chosenRef || own;
  const lastReset = useRef(resetKey);
  if (lastReset.current !== resetKey) { lastReset.current = resetKey; chosen.current = false; }
  useEffect(() => {
    const ids = (data?.choices || []).map((s) => s.id);
    const v = value || "";
    if (chosen.current && v && (ids.includes(v) || (keepStored && v === (stored || "")))) return;
    let next;
    if (keepStored) next = stored || "";
    else if (data) next = data.picked || "";
    else return;
    // The form is about to show a school the person did not choose, so it no
    // longer counts as their choice (review round 8).
    chosen.current = false;
    if (next !== v) setValue(next);
  }, [data, keepStored, resetKey, value, stored]); // eslint-disable-line
  return () => { chosen.current = true; };
}
